import { z } from 'zod';

/** Sistemi esecutori dell'azienda: configurazione del lavoro, senza avvio automatico. */
export const SYSTEM_KINDS = ['software', 'automation', 'ai_agent'] as const;
export type SystemKind = (typeof SYSTEM_KINDS)[number];

const unique = (values: string[]) => new Set(values).size === values.length;
const operation = z.string().regex(/^[a-z][a-z0-9_]{0,62}$/);

export const SystemRoleInputSchema = z.object({
  role_key: z.string().trim().min(1).max(120),
  /** null = l'intero ambito del ruolo; altrimenti un sottoinsieme delle sue sedi. */
  site_ids: z.array(z.string().uuid()).min(1).max(100).refine(unique, 'Una sede compare più volte').nullable().default(null),
});
export type SystemRoleInput = z.infer<typeof SystemRoleInputSchema>;

export const SystemConnectionInputSchema = z.object({
  connection_id: z.string().uuid(),
  /** Operazioni già presenti nella definizione dell'utenza, scelte dal responsabile. */
  reads: z.array(operation).max(100).refine(unique, 'Una lettura compare più volte').default([]),
  writes: z.array(operation).max(100).refine(unique, 'Una scrittura compare più volte').default([]),
});
export type SystemConnectionInput = z.infer<typeof SystemConnectionInputSchema>;

export const CreateSystemExecutorSchema = z.object({
  name: z.string().trim().min(1).max(120),
  kind: z.enum(SYSTEM_KINDS),
  active: z.boolean().default(true),
  manager_id: z.string().uuid().nullable().default(null),
  notes: z.string().trim().max(2000).nullable().default(null),
  roles: z.array(SystemRoleInputSchema).max(100).refine((rows) => unique(rows.map((r) => r.role_key)), 'Un ruolo compare più volte').default([]),
  connections: z.array(SystemConnectionInputSchema).max(50).refine((rows) => unique(rows.map((r) => r.connection_id)), 'Un’utenza compare più volte').default([]),
});
export type CreateSystemExecutorInput = z.infer<typeof CreateSystemExecutorSchema>;
export const UpdateSystemExecutorSchema = CreateSystemExecutorSchema.partial();
export type UpdateSystemExecutorInput = z.infer<typeof UpdateSystemExecutorSchema>;

export interface SystemExecutor {
  id: string;
  name: string;
  kind: SystemKind;
  active: boolean;
  manager_id: string | null;
  manager_name: string | null;
  notes: string | null;
  roles: Array<SystemRoleInput & { role_name: string; sites: string[] }>;
  connections: Array<SystemConnectionInput & { software_name: string; label: string | null; status: string }>;
  /** Completezza della configurazione: non abilita l'esecuzione automatica. */
  readiness: 'ready' | 'incomplete' | 'inactive';
  missing: string[];
  created_at: string;
  updated_at: string;
}

export interface SystemOptions {
  people: Array<{ id: string; name: string }>;
  roles: Array<{ key: string; label: string; site_ids: string[] | null }>;
  sites: Array<{ id: string; name: string }>;
  connections: Array<{
    id: string;
    label: string | null;
    software_name: string;
    status: string;
    reads: Array<{ key: string; label: string }>;
    writes: Array<{ key: string; label: string }>;
  }>;
}
